import { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import dayjs from "dayjs";
import { accessTokenInfoAtom } from "../store";
import { resultBaseTimeAtom } from "../components/atoms/ResultAnalysis/ResultAtom";
import {
  fetchMonthlyReport,
  fetchMonthlyReportAvailable,
  MonthlyReportContent,
} from "../stores/monthlyReportStore";
import { useToastMessage } from "./useToastMessage";

// src/utils/queryKeyFactory.ts は参照権限が無いため、キーはここで定義している
// キャッシュは無期限かつログイン時に消去されないため、再ログイン後に前回の結果を
// 返さないようアクセストークンをキーに含める
const MONTHLY_REPORT_KEYS = {
  available: (accessToken: string) =>
    ["monthlyReport", accessToken, "available"] as const,
  detail: (accessToken: string, yearMonth: string) =>
    ["monthlyReport", accessToken, "detail", yearMonth] as const,
};

// 画面を開いている間はキャッシュを再利用し、月送り・タブ往復での再取得を行わない
// (最新化はページ再読み込み時)
const MONTHLY_REPORT_CACHE = {
  staleTime: Infinity,
  gcTime: Infinity,
} as const;

type UseMonthlyReportValue = {
  /** 利用可能月一覧の取得中 */
  isAvailableLoading: boolean;
  /** 利用可能月一覧の取得に失敗した */
  isAvailableError: boolean;
  /** 利用可能月が0件(MI1データが一度も無い) */
  isEmpty: boolean;
  /** 表示中の年月(YYYY-MM)。未確定なら null */
  yearMonth: string | null;
  /** 表示中の年月が利用可能月に含まれない */
  isNoDataMonth: boolean;
  /** 表示中の年月のレポート本文。取得中・失敗時・利用可能月に含まれない月は null */
  report: MonthlyReportContent | null;
  /** 表示中の年月のレポート取得中 */
  isReportLoading: boolean;
  /** より古い月へ移動できるか */
  canDecrement: boolean;
  /** より新しい月へ移動できるか */
  canIncrement: boolean;
  /** より古い月へ移動する */
  decrement: () => void;
  /** より新しい月へ移動する */
  increment: () => void;
};

/**
 * 月間レポートの取得と月送りを扱う
 * 取得済みの月はキャッシュから返し、再取得しない(MONTHLY_REPORT_CACHE)
 */
export function useMonthlyReport(): UseMonthlyReportValue {
  const toastError = useToastMessage();
  const [tokenInfo] = useAtom(accessTokenInfoAtom);
  const accessToken = tokenInfo?.access_token ?? null;
  // 表示月は推移グラフと共有する
  const [baseTime, setBaseTime] = useAtom(resultBaseTimeAtom);
  const baseYearMonth = baseTime.format("YYYY-MM");

  const availableQuery = useQuery({
    queryKey: MONTHLY_REPORT_KEYS.available(accessToken ?? ""),
    queryFn: fetchMonthlyReportAvailable,
    enabled: accessToken !== null,
    ...MONTHLY_REPORT_CACHE,
  });

  // 降順(先頭が最新月)
  const availableYearMonths = useMemo(
    () => availableQuery.data ?? [],
    [availableQuery.data],
  );
  // 最古の解析月から前月までを降順で並べる
  // 利用可能月に含まれない月(契約が無い月など)も選択できる
  // YYYY-MM は文字列比較で大小が決まる
  const oldestYearMonth = availableYearMonths[availableYearMonths.length - 1];
  const yearMonths = useMemo(() => {
    if (oldestYearMonth === undefined) return [];
    const values: string[] = [];
    for (
      let month = dayjs().subtract(1, "month");
      month.format("YYYY-MM") >= oldestYearMonth;
      month = month.subtract(1, "month")
    ) {
      values.push(month.format("YYYY-MM"));
    }
    return values;
  }, [oldestYearMonth]);
  // グラフ月が範囲外(当月など)のときは、範囲内で最も近い月を表示する
  let yearMonth: string | null = null;
  if (yearMonths.length > 0) {
    if (baseYearMonth > yearMonths[0]) yearMonth = yearMonths[0];
    else if (baseYearMonth < yearMonths[yearMonths.length - 1])
      yearMonth = yearMonths[yearMonths.length - 1];
    else yearMonth = baseYearMonth;
  }
  // 利用可能月に含まれない月はAPIを呼ばずにデータなしとして表示する
  const isNoDataMonth =
    yearMonth !== null && !availableYearMonths.includes(yearMonth);

  const reportQuery = useQuery({
    queryKey: MONTHLY_REPORT_KEYS.detail(accessToken ?? "", yearMonth ?? ""),
    queryFn: () => fetchMonthlyReport(yearMonth!),
    enabled: accessToken !== null && yearMonth !== null && !isNoDataMonth,
    ...MONTHLY_REPORT_CACHE,
  });

  // エラーはトーストのみで通知する(本文位置は空のまま)
  useEffect(() => {
    if (availableQuery.isError) toastError(availableQuery.error);
  }, [availableQuery.isError, availableQuery.error, toastError]);
  useEffect(() => {
    if (reportQuery.isError) toastError(reportQuery.error);
  }, [reportQuery.isError, reportQuery.error, toastError]);

  // 一覧は降順なので index+1 がより古い月、index-1 がより新しい月
  const index = yearMonth === null ? -1 : yearMonths.indexOf(yearMonth);
  const canDecrement = index >= 0 && index < yearMonths.length - 1;
  const canIncrement = index > 0;

  // グラフの月送り(modifyMonthAtom)と同じく15日を基準日にする
  const decrement = useCallback(() => {
    if (index >= 0 && index < yearMonths.length - 1)
      setBaseTime(dayjs(yearMonths[index + 1]).date(15));
  }, [index, yearMonths, setBaseTime]);

  const increment = useCallback(() => {
    if (index > 0) setBaseTime(dayjs(yearMonths[index - 1]).date(15));
  }, [index, yearMonths, setBaseTime]);

  return {
    isAvailableLoading: availableQuery.isPending,
    isAvailableError: availableQuery.isError,
    isEmpty: availableQuery.isSuccess && availableYearMonths.length === 0,
    yearMonth,
    isNoDataMonth,
    report: reportQuery.data?.report ?? null,
    isReportLoading:
      yearMonth !== null && !isNoDataMonth && reportQuery.isPending,
    canDecrement,
    canIncrement,
    decrement,
    increment,
  };
}
